import { Link } from "wouter";
import { Shield, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link href="/signup">
            <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700 -ml-2 mb-4">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Shield className="w-5 h-5 text-indigo-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900">Privacy Policy</h1>
          </div>
          <p className="text-sm text-slate-500">Last updated: January 1, 2025</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-100">

          <section className="px-8 py-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">1. Data Collection</h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              We are committed to protecting your privacy. This Privacy Policy explains what data we collect,
              how we use it, and the controls you have over your information. We collect only the minimum
              information necessary to provide our services.
            </p>
            <p className="text-slate-600 text-sm leading-relaxed mt-3">
              We collect information you provide directly (such as when creating an account), information
              generated through your use of the platform, and limited technical data required to operate the
              service securely.
            </p>
          </section>

          <section className="px-8 py-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">2. Account Information</h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              When you create an account, we collect your name, email address, organization name, and
              authentication credentials. This information is necessary to provide you with access to the
              platform and to communicate important service updates.
            </p>
            <ul className="mt-3 space-y-2 text-sm text-slate-600 list-disc list-inside">
              <li>Name and email address for account identification and communication.</li>
              <li>Organization details for workspace management.</li>
              <li>Billing information, processed and stored securely by our payment provider (Stripe).</li>
              <li>Profile settings and preferences you configure within the platform.</li>
            </ul>
          </section>

          <section className="px-8 py-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">3. Usage Data</h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              We automatically collect certain information about how you interact with the platform. This
              includes log data (IP addresses, browser type, pages visited, timestamps), feature usage
              patterns, and performance metrics used to improve the service.
            </p>
            <p className="text-slate-600 text-sm leading-relaxed mt-3">
              Conversation data processed through your chatbots is stored securely and used only to provide
              the service. We do not use your chatbot conversation content to train our AI models without
              your explicit consent.
            </p>
          </section>

          <section className="px-8 py-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">4. Security</h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              We implement industry-standard security measures to protect your data, including encryption at
              rest and in transit (TLS 1.2+), access controls, audit logging, and regular security reviews.
            </p>
            <ul className="mt-3 space-y-2 text-sm text-slate-600 list-disc list-inside">
              <li>All sensitive data is encrypted at rest using AES-256.</li>
              <li>Access to production systems is restricted to authorized personnel only.</li>
              <li>We conduct periodic security assessments and vulnerability scans.</li>
              <li>In the event of a data breach, we will notify affected users within 72 hours.</li>
            </ul>
          </section>

          <section className="px-8 py-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">5. Cookies</h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              We use strictly necessary cookies to maintain your session and authentication state. We also use
              functional cookies to remember your preferences (such as theme and language settings). We do not
              use third-party advertising or tracking cookies.
            </p>
            <p className="text-slate-600 text-sm leading-relaxed mt-3">
              You can manage cookie preferences through your browser settings. Disabling strictly necessary
              cookies may prevent you from using core features of the platform.
            </p>
          </section>

          <section className="px-8 py-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">6. Data Sharing</h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              We do not sell, rent, or trade your personal information to third parties. We share data only
              with trusted service providers who assist in operating the platform, under strict data processing
              agreements.
            </p>
            <p className="text-slate-600 text-sm leading-relaxed mt-3">
              We may disclose your information if required by law, court order, or to protect the rights and
              safety of our users or the public. We will notify you of such disclosures where legally permitted.
            </p>
            <ul className="mt-3 space-y-2 text-sm text-slate-600 list-disc list-inside">
              <li>Payment processing via Stripe (PCI-DSS compliant).</li>
              <li>AI model providers for processing chatbot queries (anonymized where possible).</li>
              <li>Infrastructure providers for hosting and data storage.</li>
            </ul>
          </section>

          <section className="px-8 py-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">7. Contact Information</h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              If you have questions about this Privacy Policy, want to exercise your data rights, or wish to
              request deletion of your data, please reach out to our privacy team:
            </p>
            <div className="mt-3 p-4 bg-slate-50 rounded-lg text-sm text-slate-600 space-y-1">
              <p><strong>Email:</strong> privacy@example.com</p>
              <p><strong>Address:</strong> 123 Platform Street, Suite 100, San Francisco, CA 94103</p>
              <p><strong>Data requests:</strong> We process data deletion requests within 30 days.</p>
            </div>
          </section>

        </div>

        <p className="text-center text-xs text-slate-400 mt-8">
          Your privacy matters to us. This policy is reviewed and updated periodically to reflect changes in our practices.
        </p>
      </div>
    </div>
  );
}
